import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { shipmentAPI } from '../../services/api';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING_QUOTE', label: 'Pending Quote' },
  { value: 'QUOTED', label: 'Quoted' },
  { value: 'QUOTE_CONFIRMED', label: 'Confirmed' },
  { value: 'BOOKED', label: 'Booked' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'DELIVERED', label: 'Delivered' }
];

const STATUS_BADGE_CLASS = {
  PENDING_QUOTE: 'badge-pending-quote',
  QUOTED: 'badge-quoted',
  QUOTE_CONFIRMED: 'badge-confirmed',
  BOOKED: 'badge-booked',
  IN_TRANSIT: 'badge-in-transit',
  DELIVERED: 'badge-delivered'
};

const ShipmentList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

  useEffect(() => {
    const fetchShipments = async () => {
      try {
        setLoading(true);
        const filters = {};
        if (statusFilter) filters.status = statusFilter;
        const result = await shipmentAPI.getAll(filters);
        setShipments(result.data || []);
      } catch (err) {
        setError('Failed to load shipments');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();
  }, [statusFilter]);

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    if (value) {
      setSearchParams({ status: value });
    } else {
      setSearchParams({});
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCost = (cost) => {
    if (!cost) return '-';
    return `$${Number(cost).toLocaleString()}`;
  };

  if (loading) return <div className="loading">Loading shipments...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="shipment-list">
      <div className="page-header">
        <h1>Shipments</h1>
        <Link to="/shipments/new" className="btn btn-primary">New Shipment</Link>
      </div>

      {/* Status filter tabs */}
      <div className="status-tabs">
        {STATUS_OPTIONS.map(opt => (
          <button
            key={opt.value}
            className={`status-tab ${statusFilter === opt.value ? 'active' : ''}`}
            onClick={() => handleStatusChange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {shipments.length === 0 ? (
        <div className="empty-state">
          <p>No shipments found{statusFilter ? ` with status "${statusFilter}"` : ''}</p>
        </div>
      ) : (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Route</th>
              <th>Status</th>
              <th>Container</th>
              <th>Cost</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map(shipment => (
              <tr key={shipment.id}>
                <td>
                  <Link to={`/shipments/${shipment.id}`} className="provider-name">
                    {shipment.orderId || shipment.id.slice(0, 8)}
                  </Link>
                </td>
                <td>{shipment.originPort} → {shipment.destinationPort}</td>
                <td>
                  <span className={`badge ${STATUS_BADGE_CLASS[shipment.status] || ''}`}>
                    {shipment.status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td>{shipment.containerCount}x {shipment.containerType}</td>
                <td>{formatCost(shipment.actualFreightCost || shipment.estimatedFreightCost)}</td>
                <td>{formatDate(shipment.createdAt)}</td>
                <td className="actions">
                  <Link to={`/shipments/${shipment.id}`} className="btn btn-sm btn-secondary">
                    View
                  </Link>
                  {shipment.status === 'QUOTED' && (
                    <Link
                      to={`/shipments/${shipment.id}/quotes/compare`}
                      className="btn btn-sm btn-primary"
                    >
                      Compare
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ShipmentList;
